<template>
  <div
    :class="[
      'sam-pagination',
      { 'is-hidden': !showPagination },
      { 'has-info': hasPageInformation },
    ]"
    data-samora
  >
    <div v-if="hasPageInformation">
      {{ getItemRange }}
    </div>

    <div class="sam-pagination__info">
      <div class="mr-4">
        {{ paginationText }}
      </div>

      <SamButton
        type="button"
        sam-type="text"
        sam-style="primary"
        :disabled="disabled || !canNavigateBack"
        icon="arrow-nav-left"
        title="Previous page"
        @click="navigate(DIRECTIONS.BACK)"
        @keydown.left="navigate(DIRECTIONS.BACK)"
      ></SamButton>

      <SamButton
        type="button"
        sam-type="text"
        sam-style="primary"
        icon="arrow-nav-right"
        title="Next page"
        data-testid="sam-pagination__next"
        :disabled="disabled || !canNavigateForward"
        @click="navigate(DIRECTIONS.FORWARD)"
        @keydown.right="navigate(DIRECTIONS.FORWARD)"
      ></SamButton>
    </div>
  </div>
</template>

<script>
import SamButton from '../SamButton/SamButton.vue';

const EVENTS = {
  CHANGE_PAGE: 'change-page',
};

const DIRECTIONS = {
  BACK: 'back',
  FORWARD: 'forward',
};

export default {
  name: 'SamPagination',
  components: {
    SamButton,
  },
  props: {
    /**
     * The value (index) of the current page
     */
    currentPage: {
      type: Number,
      default: 0,
    },
    /**
     * The total number of available pages
     */
    totalPages: {
      type: Number,
      default: undefined,
    },
    /**
     * The number of items
     */
    paginationItems: {
      type: Number,
      default: 10,
    },
    /**
     * Flag to determine if the component is in a loading state
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag to determine if the component is in the disabled state
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag to determine whether the component should be visible or not while loading
     * NOTE: this property changes visibility meaning that the component will not be removed from the dom
     */
    autoHide: {
      type: Boolean,
      default: true,
    },
    immediate: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag to determine if the component is on the last page
     */
    isLastPage: {
      type: Boolean,
      default: false,
    },
    showPageInfo: {
      type: Boolean,
      required: false,
      default: false,
    },
    itemsText: {
      type: String,
      required: false,
      default: '',
    },
    totalItemsCount: {
      type: Number,
      required: false,
      default: null,
    },
  },
  data: () => ({
    DIRECTIONS,
    iconSize: 'sm',
    actualCurrentPage: 0,
  }),
  computed: {
    canNavigateBack() {
      return !this.loading && this.actualCurrentPage > 0;
    },
    canNavigateForward() {
      return (
        !this.loading &&
        (!this.totalPages || this.actualCurrentPage < this.totalPages - 1) &&
        !this.isLastPage
      );
    },
    showPagination() {
      return (
        this.totalPages === undefined || !this.autoHide || (!this.loading && this.totalPages > 1)
      );
    },
    page() {
      return this.actualCurrentPage + 1;
    },
    paginationText() {
      let pageStr = `Page ${this.page}`;

      if (this.totalPages) {
        pageStr += ` of ${this.totalPages}`;
      }

      return pageStr;
    },

    hasPageInformation() {
      return this.page && this.paginationItems && this.showPageInfo && this.totalItemsCount;
    },
    getItemRange() {
      const itemsPerPage = this.paginationItems;
      const startItem = (this.page - 1) * itemsPerPage + 1;
      let endItem = this.page * itemsPerPage;

      if (endItem > this.totalItemsCount) {
        endItem = this.totalItemsCount;
      }

      return `${startItem} - ${endItem} of ${this.totalItemsCount} ${this.itemsText}`;
    },
  },
  watch: {
    currentPage(newValue) {
      this.actualCurrentPage = newValue;
    },
  },
  mounted() {
    this.actualCurrentPage = this.currentPage;
  },
  methods: {
    navigate(direction) {
      let newPage = this.actualCurrentPage;

      newPage = direction === DIRECTIONS.FORWARD ? (newPage += 1) : (newPage -= 1);

      if (this.immediate) {
        this.actualCurrentPage = newPage;
      }

      /**
       * Tab changed event
       * @event change-page
       * @property {number} page
       */
      this.$emit(EVENTS.CHANGE_PAGE, newPage);
    },
  },
};
</script>

<style lang="scss" scoped>
.sam-pagination {
  @extend .text--body-2, .text--light, .d-flex, .middle-xs, .end-xs, .p-4;
  visibility: visible;

  &.has-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &.is-hidden {
    visibility: hidden;
  }

  &__info {
    display: flex;
    align-items: center;
  }

  &__navigation {
    border: none;
    box-shadow: none;
    background-color: transparent;
    outline: none;
    transition: background-color 0.2s;

    &:focus,
    &:active {
      outline: auto;
    }

    &:hover:enabled {
      cursor: pointer;
      background-color: $neutral--30;
      transition: background-color 0.2s;
    }

    &:disabled {
      cursor: not-allowed;

      svg {
        fill: $neutral--40;
      }
    }

    svg {
      pointer-events: none;
    }
  }
}
</style>
